import { css } from 'styled-components';
import { CONF_KEY, DEFAULT_CONF, DIMENSIONS } from '../defaultConfig.js';

const configCache = new Map();
const internalConfigCache = new Map();
const getConfig = theme => {
  if (typeof theme === 'object' && CONF_KEY in theme) {
    if (configCache.has(theme[CONF_KEY])) {
      return configCache.get(theme[CONF_KEY]);
    }
    const conf = mergeDeep(DEFAULT_CONF, theme[CONF_KEY]);
    configCache.set(theme[CONF_KEY], conf);
    return conf;
  }
  throw new Error('Theme must be an grid config theme.');
};
const isNotNullOrUndefined = input => input !== null && typeof input !== 'undefined';
const getInternalConfig = theme => {
  const confKey = theme[CONF_KEY];
  if (!confKey) throw new Error('Theme must be a grid config theme.');
  if (internalConfigCache.has(confKey)) return internalConfigCache.get(confKey);
  const conf = {
    ...DEFAULT_CONF,
    ...theme[CONF_KEY]
  };
  ['columns', 'columnGap', 'containerPadding'].forEach(prop => conf[prop] = mergeScreens(conf[prop]));
  conf.container = mergeScreens(conf.container);
  internalConfigCache.set(confKey, conf);
  return conf;
};
const mergeScreens = conf => {
  let lastValue = 0;
  return DIMENSIONS.map(screenSize => {
    if (screenSize === 'xs' && conf[screenSize] === undefined || screenSize === 'xs' && conf[screenSize] === lastValue) {
      return {
        key: screenSize,
        val: lastValue
      };
    }
    if (conf[screenSize] === undefined || conf[screenSize] === lastValue) {
      return null;
    }
    lastValue = conf[screenSize];
    return {
      key: screenSize,
      val: lastValue
    };
  }).filter(isNotNullOrUndefined).reduce((acc, {
    key,
    val
  }) => {
    acc[key] = val;
    return acc;
  }, {});
};
const mediaInternal = (screenSize, theme) => {
  if (typeof theme === 'object' && !(CONF_KEY in theme)) throw new Error('Theme must be a grid config theme.');
  return (...args) => css`
        @media ${generateMediaString(screenSize, theme)} {
            ${css(...args)}
        }
    `;
};
const generateMediaString = (screenSize, theme) => {
  if (theme) {
    const conf = getConfig(theme);
    return `${conf.mediaQuery}${conf.breakpoints[screenSize] >= 0 ? ` and (min-width: ${conf.breakpoints[screenSize]}px)` : ''}`;
  }
  return ({
    theme: internalTheme
  }) => {
    const conf = getConfig(internalTheme);
    return `${conf.mediaQuery}${conf.breakpoints[screenSize] >= 0 ? ` and (min-width: ${conf.breakpoints[screenSize]}px)` : ''}`;
  };
};
const generateMediaStringBetween = (screenSizeMin, screenSizeMax, theme) => {
  if (theme) {
    const conf = getConfig(theme);
    return `${conf.mediaQuery} and (min-width: ${conf.breakpoints[screenSizeMin]}px) and (max-width: ${conf.breakpoints[screenSizeMax] - 1}px)`;
  }
  return ({
    theme: internalTheme
  }) => {
    const conf = getConfig(internalTheme);
    return `${conf.mediaQuery} and (min-width: ${conf.breakpoints[screenSizeMin]}px) and (max-width: ${conf.breakpoints[screenSizeMax] - 1}px)`;
  };
};
const isObject = item => item && typeof item === 'object' && !Array.isArray(item);
const mergeDeep = (target, source) => {
  if (!source) {
    return target;
  }
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, {
            [key]: {}
          });
        }
        mergeDeep(target[key], source[key]);
      } else {
        Object.assign(target, {
          [key]: source[key]
        });
      }
    });
  }
  return target;
};
const getResponsiveText = (screenTexts, screenSize) => {
  if (screenTexts.xs === null || screenTexts.xs === undefined) {
    throw new Error('The xs breakpoint must be defined.');
  }
  const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];
  let text = screenTexts.xs;
  for (const size of breakpoints) {
    if (screenTexts[size] !== null && screenTexts[size] !== undefined) {
      text = screenTexts[size];
    }
    if (size === screenSize) {
      break;
    }
  }
  return text;
};

export { generateMediaString, generateMediaStringBetween, getConfig, getInternalConfig, getResponsiveText, isObject, mediaInternal, mergeScreens };
//# sourceMappingURL=lib.js.map
